import React, { useState, useEffect, useRef, useCallback } from 'react';
import { icons } from './adminSidebarIcons';
import DonutChart from './DonutChart';
import { FiBookOpen, FiCalendar, FiCheckCircle, FiClock, FiEdit3, FiFileText, FiGrid, FiHelpCircle, FiTag, FiUser, FiXCircle } from 'react-icons/fi';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { API_BASE_URL } from '../config/apiConfig';
import buildImageUrl from '../config/imageUrlHelper';
import { AnimatePresence, motion } from 'framer-motion';
import useLockBodyScroll from '../hooks/useLockBodyScroll';

const ESTADOS_CONFIG = {
  Aceptada: {
    color: '#22c55e',
    badge: 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-white',
    card: 'bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-700/60'
  },
  Rechazada: {
    color: '#ef4444',
    badge: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-white',
    card: 'bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-700/60'
  },
  'En proceso': {
    color: '#f59e0b',
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-white',
    card: 'bg-amber-50 border-amber-200 dark:bg-amber-900/30 dark:border-amber-700/60'
  },
  Modificada: {
    color: '#3b82f6',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-white',
    card: 'bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:border-blue-700/60'
  },
  Desconocido: {
    color: '#64748b',
    badge: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-white',
    card: 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-600'
  }
};

const ESTADOS_ORDEN = ['Aceptada', 'Rechazada', 'En proceso', 'Modificada'];

const DIFICULTAD_CONFIG = {
  facil: {
    label: 'Facil',
    headerGradient: 'from-emerald-500 to-teal-500 dark:from-emerald-600 dark:to-teal-600',
    difficultyBadge: 'bg-white/20 text-white border border-white/30'
  },
  medio: {
    label: 'Media',
    headerGradient: 'from-amber-500 to-orange-500 dark:from-amber-600 dark:to-orange-600',
    difficultyBadge: 'bg-white/20 text-white border border-white/30'
  },
  dificil: {
    label: 'Dificil',
    headerGradient: 'from-rose-500 to-fuchsia-500 dark:from-rose-600 dark:to-fuchsia-600',
    difficultyBadge: 'bg-white/20 text-white border border-white/30'
  },
  desconocido: {
    label: 'No definida',
    headerGradient: 'from-slate-500 to-slate-600 dark:from-slate-600 dark:to-slate-700',
    difficultyBadge: 'bg-white/20 text-white border border-white/30'
  }
};

const MisPreguntas = () => {
  const [preguntas, setPreguntas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todas');
  const [cantidadVisible, setCantidadVisible] = useState('5');
  const [busqueda, setBusqueda] = useState('');
  const [preguntaSeleccionada, setPreguntaSeleccionada] = useState(null);
  useLockBodyScroll(Boolean(preguntaSeleccionada));
  const [mostrarGraficoMobile, setMostrarGraficoMobile] = useState(false);
  const rootRef = useRef(null);
  const graficoMobileRef = useRef(null);
  const detalleAbiertoPrevioRef = useRef(false);
  const { user, loading: userLoading } = useUser();

  const esVistaMobile = useCallback(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches, []);

  const asegurarPanelVisible = useCallback(({ behavior = 'auto' } = {}) => {
    if (!esVistaMobile() || !rootRef.current) return;

    const scrollContainer = rootRef.current.closest('[data-admin-panel-scroll="true"]');
    const header = document.querySelector('[data-app-header="true"]');

    if (!scrollContainer || !header) return;

    const safeTop = header.getBoundingClientRect().bottom + 12;
    const panelTop = rootRef.current.getBoundingClientRect().top;

    if (panelTop < safeTop) {
      const nextTop = Math.max(scrollContainer.scrollTop + (panelTop - safeTop), 0);
      scrollContainer.scrollTo({ top: nextTop, behavior });
    }
  }, [esVistaMobile]);

  useEffect(() => {
    // Solo consultar cuando la sesión está resuelta y hay usuario autenticado.
    if (!userLoading && user) {
      fetchPreguntas();
    }
  }, [user, userLoading]);

  useEffect(() => {
    if (!esVistaMobile()) return;

    const timer = setTimeout(() => {
      asegurarPanelVisible({ behavior: 'smooth' });
    }, 180);

    return () => clearTimeout(timer);
  }, [asegurarPanelVisible, esVistaMobile, mostrarGraficoMobile]);

  useEffect(() => {
    if (!esVistaMobile()) {
      detalleAbiertoPrevioRef.current = Boolean(preguntaSeleccionada);
      return;
    }

    const detalleEstabaAbierto = detalleAbiertoPrevioRef.current;
    const detalleAbiertoAhora = Boolean(preguntaSeleccionada);

    if (detalleEstabaAbierto && !detalleAbiertoAhora) {
      const timer = setTimeout(() => {
        asegurarPanelVisible({ behavior: 'auto' });
      }, 0);

      detalleAbiertoPrevioRef.current = detalleAbiertoAhora;
      return () => clearTimeout(timer);
    }

    detalleAbiertoPrevioRef.current = detalleAbiertoAhora;
  }, [asegurarPanelVisible, esVistaMobile, preguntaSeleccionada]);

  const fetchPreguntas = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/mis_preguntas.php`);
      // Asegurarse de que response.data es un array
      if (Array.isArray(response.data)) {
        setPreguntas(response.data);
      } else {
        console.error('La respuesta no es un array:', response.data);
        setPreguntas([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar preguntas:', error);
      setPreguntas([]);
      setLoading(false);
    }
  };

  // Función para obtener el estado de la pregunta
  // El estado 2 indica que la pregunta ha sido modificada y está pendiente de revisión
  const obtenerEstado = (aprobada) => {
    if (aprobada === 2) return 'Modificada'; // Pregunta modificada que requiere revisión
    if (aprobada === 1) return 'Aceptada'; // Pregunta aprobada
    if (aprobada === 0) return 'En proceso'; // Pregunta pendiente de revisión
    if (aprobada === -1) return 'Rechazada'; // Pregunta rechazada
    return 'Desconocido';
  };

  const obtenerClasesEstado = (estado) => ESTADOS_CONFIG[estado] || ESTADOS_CONFIG.Desconocido;

  const obtenerIconoEstado = (estado) => {
    if (estado === 'Aceptada') return <FiCheckCircle className="text-green-700 dark:text-green-300" aria-hidden="true" />;
    if (estado === 'Rechazada') return <FiXCircle className="text-red-700 dark:text-red-300" aria-hidden="true" />;
    if (estado === 'En proceso') return <FiClock className="text-amber-700 dark:text-amber-300" aria-hidden="true" />;
    if (estado === 'Modificada') return <FiEdit3 className="text-blue-700 dark:text-blue-300" aria-hidden="true" />;
    return <FiBookOpen className="text-slate-700 dark:text-slate-300" aria-hidden="true" />;
  };

  const textoBusqueda = busqueda.trim().toLowerCase();

  const resumenEstados = ESTADOS_ORDEN.map((estado) => ({
    estado,
    total: preguntas.filter((pregunta) => obtenerEstado(pregunta.aprobada) === estado).length,
    ...obtenerClasesEstado(estado)
  }));

  const preguntasFiltradas = preguntas.filter((pregunta) => {
    const estado = obtenerEstado(pregunta.aprobada);
    const estadoCoincide = filtroEstado === 'todas' || estado === filtroEstado;
    const coincideBusqueda = !textoBusqueda || [
      pregunta.pregunta,
      pregunta.respuesta_correcta,
      pregunta.categoria,
      pregunta.etiqueta,
      estado
    ].some((valor) => (valor || '').toString().toLowerCase().includes(textoBusqueda));

    return estadoCoincide && coincideBusqueda;
  });

  const preguntasVisibles = cantidadVisible === 'todas'
    ? preguntasFiltradas
    : preguntasFiltradas.slice(0, Number(cantidadVisible));

  const totalPreguntas = resumenEstados.reduce((acc, item) => acc + item.total, 0);
  const hayDatosGrafico = totalPreguntas > 0;
  const datosGrafico = resumenEstados.map((item) => ({
    label: item.estado,
    value: item.total,
    color: item.color
  }));

  const aplicarFiltroEstado = (estado) => {
    setFiltroEstado(estado);
  };

  const obtenerConfigDificultad = (dificultad) => {
    const valor = (dificultad || '').toString().toLowerCase();
    if (valor === 'facil') return DIFICULTAD_CONFIG.facil;
    if (valor === 'medio') return DIFICULTAD_CONFIG.medio;
    if (valor === 'dificil') return DIFICULTAD_CONFIG.dificil;
    return DIFICULTAD_CONFIG.desconocido;
  };

  const resumirTexto = (texto, maximo = 220) => {
    const limpio = (texto || '').toString().trim();
    if (!limpio) return '-';
    if (limpio.length <= maximo) return limpio;
    return `${limpio.slice(0, maximo)}...`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="dark:bg-gray-800 dark:text-white transition-colors duration-300 overflow-x-hidden pr-1">
      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 dark:text-white">
        <span className="text-xl">{icons['mis-preguntas']}</span>
        Mis Preguntas
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
        <section className="bg-white rounded-lg shadow-md p-4 sm:p-6 dark:bg-gray-800 dark:text-white transition-colors duration-300">
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h4 className="text-base sm:text-lg font-medium dark:text-white">Historial de preguntas</h4>
              <p className="text-sm text-gray-500 dark:text-gray-300 mt-1">
                Mostrando {preguntasVisibles.length} de {preguntasFiltradas.length} preguntas filtradas
              </p>
            </div>

            <div className="sm:hidden w-full">
              <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)] items-end gap-2">
                <div className="min-w-0">
                  <label className="block text-gray-700 mb-1 dark:text-gray-300 text-xs">Buscar</label>
                  <input
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Pregunta..."
                    className="w-full p-2 text-xs border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-1 dark:text-gray-300 text-xs">Mostrar</label>
                  <select
                    value={cantidadVisible}
                    onChange={(e) => setCantidadVisible(e.target.value)}
                    className="w-full p-2 text-xs border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="todas">Todas</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 mb-1 dark:text-gray-300 text-xs">Estado</label>
                  <select
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value)}
                    className="w-full p-2 text-xs border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
                  >
                    <option value="todas">Todas</option>
                    <option value="En proceso">En proceso</option>
                    <option value="Aceptada">Aceptada</option>
                    <option value="Rechazada">Rechazada</option>
                    <option value="Modificada">Modificada</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="hidden sm:grid grid-cols-1 sm:grid-cols-3 gap-3 w-full lg:w-auto">
              <div className="w-full">
                <label className="block text-gray-700 mb-2 dark:text-gray-300 text-sm">Buscar</label>
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Pregunta, respuesta, categoría..."
                  className="w-full p-2 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
                />
              </div>
              <div className="w-full">
                <label className="block text-gray-700 mb-2 dark:text-gray-300 text-sm">Mostrar</label>
                <select
                  value={cantidadVisible}
                  onChange={(e) => setCantidadVisible(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
                >
                  <option value="5">5 preguntas</option>
                  <option value="10">10 preguntas</option>
                  <option value="20">20 preguntas</option>
                  <option value="todas">Todas</option>
                </select>
              </div>
              <div className="w-full">
                <label className="block text-gray-700 mb-2 dark:text-gray-300 text-sm">Filtrar por Estado</label>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
                >
                  <option value="todas">Todas</option>
                  <option value="En proceso">En proceso</option>
                  <option value="Aceptada">Aceptada</option>
                  <option value="Rechazada">Rechazada</option>
                  <option value="Modificada">Modificada</option>
                </select>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-700">
              <colgroup>
                <col className="w-[46%] sm:w-[38%]" />
                <col className="w-[34%] sm:w-[32%]" />
                <col className="hidden sm:table-column sm:w-[15%]" />
                <col className="w-[20%] sm:w-[15%]" />
              </colgroup>
              <thead className="bg-gray-50 dark:bg-gray-700 dark:text-white">
                <tr>
                  <th className="px-2.5 sm:px-4 py-3 sm:py-3 text-left text-[11px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wide dark:text-gray-300">
                    <span className="inline-flex items-center gap-1.5">
                      <FiHelpCircle className="hidden sm:inline text-gray-700 dark:text-gray-200" aria-hidden="true" />
                      Pregunta
                    </span>
                  </th>
                  <th className="px-2.5 sm:px-4 py-3 sm:py-3 text-left text-[11px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wide dark:text-gray-300">
                    <span className="inline-flex items-center gap-1.5">
                      <FiCheckCircle className="hidden sm:inline text-gray-700 dark:text-gray-200" aria-hidden="true" />
                      Respuesta
                    </span>
                  </th>
                  <th className="hidden sm:table-cell px-2 sm:px-4 py-2.5 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                    <span className="inline-flex items-center gap-1.5">
                      <FiGrid className="text-gray-700 dark:text-gray-200" aria-hidden="true" />
                      Categoria
                    </span>
                  </th>
                  <th className="px-2.5 sm:px-4 py-3 sm:py-3 text-center sm:text-left text-[11px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wide dark:text-gray-300">
                    <span className="inline-flex items-center justify-center sm:justify-start gap-1.5 w-full">
                      <FiBookOpen className="hidden sm:inline text-gray-700 dark:text-gray-200" aria-hidden="true" />
                      Estado
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-600 dark:divide-gray-700 dark:text-white">
                {preguntasVisibles.length > 0 ? (
                  preguntasVisibles.map((pregunta) => {
                    const estado = obtenerEstado(pregunta.aprobada);
                    return (
                      <tr key={pregunta.id} className="dark:bg-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer" onClick={() => setPreguntaSeleccionada(pregunta)}>
                        <td className="px-2.5 sm:px-4 py-3 sm:py-3 dark:text-white text-xs sm:text-sm align-top">
                          <p
                            className="font-medium text-slate-800 dark:text-slate-100"
                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            title={pregunta.pregunta || ''}
                          >
                            {pregunta.pregunta}
                          </p>
                        </td>
                        <td className="px-2.5 sm:px-4 py-3 sm:py-3 dark:text-white text-xs sm:text-sm align-top">
                          <p
                            className="text-slate-700 dark:text-slate-200"
                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            title={pregunta.respuesta_correcta || ''}
                          >
                            {pregunta.respuesta_correcta}
                          </p>
                        </td>
                        <td className="hidden sm:table-cell px-2 sm:px-4 py-2.5 sm:py-3 dark:text-white text-xs sm:text-sm align-top">
                          <p className="truncate" title={pregunta.categoria || ''}>{pregunta.categoria}</p>
                        </td>
                        <td className="px-2.5 sm:px-4 py-3 sm:py-3 text-xs sm:text-sm align-top text-center sm:text-left">
                          <span className={`inline-block px-2 sm:px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold leading-tight ${obtenerClasesEstado(estado).badge}`}>
                            {estado}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="px-4 py-6 text-center dark:text-white text-sm">
                      No se encontraron preguntas
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden mt-4">
            <button
              type="button"
              onClick={() => setMostrarGraficoMobile((prev) => !prev)}
              className="w-full inline-flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-3 py-2 text-sm font-medium"
            >
              <span>Grafico de estado</span>
              <span className={`transition-transform ${mostrarGraficoMobile ? 'rotate-180' : ''}`}>▼</span>
            </button>

            <AnimatePresence initial={false}>
              {mostrarGraficoMobile && (
                <motion.div
                  ref={graficoMobileRef}
                  initial={{ height: 0, opacity: 0, y: -10 }}
                  animate={{ height: 'auto', opacity: 1, y: 0 }}
                  exit={{ height: 0, opacity: 0, y: -10 }}
                  transition={{ duration: 0.28, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 bg-white rounded-lg shadow-md p-4 dark:bg-gray-800 dark:text-white transition-colors duration-300">
                    <h4 className="text-base font-medium mb-4 dark:text-white">Estado de tus preguntas</h4>
                    {hayDatosGrafico ? (
                      <>
                        <div className="mb-4">
                          <DonutChart
                            data={datosGrafico}
                            showLegend={false}
                            chartHeightClass="h-44"
                            maxWidth={220}
                            onSegmentClick={(segmento) => aplicarFiltroEstado(segmento.label)}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {resumenEstados.map((item) => (
                            <div
                              key={item.estado}
                              className={`border rounded-lg px-3 py-2 flex items-start justify-between min-w-0 cursor-pointer transition-transform hover:scale-[1.02] ${item.card}`}
                              onClick={() => aplicarFiltroEstado(item.estado)}
                            >
                              <div className="flex items-start gap-2 min-w-0 pr-2">
                                <span className="shrink-0 mt-0.5">
                                  {obtenerIconoEstado(item.estado)}
                                </span>
                                <div className="min-w-0">
                                  <p className="text-xs font-medium dark:text-white leading-4 truncate">{item.estado}</p>
                                  <p className="text-[10px] text-gray-500 dark:text-gray-300 leading-4">
                                    {totalPreguntas > 0 ? `${Math.round((item.total / totalPreguntas) * 100)}% del total` : '0% del total'}
                                  </p>
                                </div>
                              </div>
                              <span className="text-sm font-semibold dark:text-white shrink-0">{item.total}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-center items-center h-40 border border-gray-300 rounded dark:border-gray-600 dark:text-white text-sm">
                        <p>Todavía no tienes preguntas registradas</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        <aside className="hidden sm:block bg-white rounded-lg shadow-md p-4 sm:p-6 dark:bg-gray-800 dark:text-white transition-colors duration-300">
          <h4 className="text-base sm:text-lg font-medium mb-4 dark:text-white">Estado de tus preguntas</h4>
          {hayDatosGrafico ? (
            <>
              <div className="mb-4">
                <DonutChart
                  data={datosGrafico}
                  showLegend={false}
                  chartHeightClass="h-44"
                  maxWidth={220}
                  onSegmentClick={(segmento) => aplicarFiltroEstado(segmento.label)}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {resumenEstados.map((item) => (
                  <div
                    key={item.estado}
                    className={`border rounded-lg px-3 py-2 flex items-start justify-between min-w-0 cursor-pointer transition-transform hover:scale-[1.02] ${item.card}`}
                    onClick={() => aplicarFiltroEstado(item.estado)}
                  >
                    <div className="flex items-start gap-2 min-w-0 pr-2">
                      <span className="shrink-0 mt-0.5">
                        {obtenerIconoEstado(item.estado)}
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-medium dark:text-white leading-4 truncate">{item.estado}</p>
                        <p className="text-[10px] text-gray-500 dark:text-gray-300 leading-4">
                          {totalPreguntas > 0 ? `${Math.round((item.total / totalPreguntas) * 100)}% del total` : '0% del total'}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold dark:text-white shrink-0">{item.total}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex justify-center items-center h-64 border border-gray-300 rounded dark:border-gray-600 dark:text-white text-sm">
              <p>Todavía no tienes preguntas registradas</p>
            </div>
          )}
        </aside>
      </div>

      {/* Modal para mostrar detalles de la pregunta */}
      {preguntaSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50" onClick={() => setPreguntaSeleccionada(null)}>
          <div className="bg-white dark:bg-gray-700 rounded-xl shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto overflow-x-hidden" onClick={e => e.stopPropagation()}>
            <div className={`bg-gradient-to-r ${obtenerConfigDificultad(preguntaSeleccionada.dificultad).headerGradient} px-5 py-4`}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <h3 className="text-xl font-semibold text-white">Tu Pregunta Enviada</h3>
                  <p className="text-blue-100 text-sm mt-1">Vista rapida y organizada de tu envio</p>
                </div>
                <button 
                  onClick={() => setPreguntaSeleccionada(null)} 
                  className="text-white/80 hover:text-white text-2xl leading-none"
                >
                  &times;
                </button>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${obtenerClasesEstado(obtenerEstado(preguntaSeleccionada.aprobada)).badge}`}>
                  {obtenerEstado(preguntaSeleccionada.aprobada)}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${obtenerConfigDificultad(preguntaSeleccionada.dificultad).difficultyBadge}`}>
                  Dificultad: {obtenerConfigDificultad(preguntaSeleccionada.dificultad).label}
                </span>
              </div>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                <div className="lg:col-span-3 space-y-3">
                  <div className="rounded-lg border border-blue-100 dark:border-gray-600 bg-blue-50/60 dark:bg-gray-800 p-3">
                    <h4 className="text-xs uppercase tracking-wide text-blue-700 dark:text-blue-300 font-semibold mb-1 inline-flex items-center gap-1.5">
                      <FiFileText className="text-blue-800 dark:text-blue-300" aria-hidden="true" />
                      Pregunta
                    </h4>
                    <p className="text-sm dark:text-white leading-5">{resumirTexto(preguntaSeleccionada.pregunta, 260)}</p>
                  </div>

                  <div className="rounded-lg border border-emerald-100 dark:border-gray-600 bg-emerald-50/70 dark:bg-gray-800 p-3">
                    <h4 className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-300 font-semibold mb-1 inline-flex items-center gap-1.5">
                      <FiCheckCircle className="text-emerald-800 dark:text-emerald-300" aria-hidden="true" />
                      Respuesta correcta
                    </h4>
                    <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200 leading-5">{resumirTexto(preguntaSeleccionada.respuesta_correcta, 180)}</p>
                  </div>

                  {preguntaSeleccionada.explicacion && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-3">
                      <h4 className="text-xs uppercase tracking-wide text-gray-600 dark:text-gray-300 font-semibold mb-1 inline-flex items-center gap-1.5">
                        <FiBookOpen className="text-gray-700 dark:text-gray-300" aria-hidden="true" />
                        Explicacion
                      </h4>
                      {preguntaSeleccionada.imagen_url && (
                        <div className="mb-3 flex justify-center">
                          <img
                            src={buildImageUrl(preguntaSeleccionada.imagen_url)}
                            alt="Imagen de la pregunta"
                            className="w-full max-w-sm max-h-44 object-contain rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700"
                            onError={(event) => {
                              event.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <p className="text-sm text-gray-700 dark:text-gray-200 leading-5">{resumirTexto(preguntaSeleccionada.explicacion, 180)}</p>
                    </div>
                  )}
                </div>

                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2 content-start">
                  <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-300 inline-flex items-center gap-1">
                      <FiGrid className="text-gray-600 dark:text-gray-300" aria-hidden="true" />
                      Categoria
                    </p>
                    <p className="text-sm font-medium dark:text-white mt-1 truncate">{preguntaSeleccionada.categoria || '-'}</p>
                  </div>

                  <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-300 inline-flex items-center gap-1">
                      <FiTag className="text-gray-600 dark:text-gray-300" aria-hidden="true" />
                      Etiqueta
                    </p>
                    <p className="text-sm font-medium dark:text-white mt-1 truncate">{preguntaSeleccionada.etiqueta || '-'}</p>
                  </div>

                  <div className="col-span-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-300 inline-flex items-center gap-1">
                      <FiCalendar className="text-gray-600 dark:text-gray-300" aria-hidden="true" />
                      Fecha
                    </p>
                    <p className="text-sm font-medium dark:text-white mt-1 truncate">
                      {preguntaSeleccionada.fecha_envio || preguntaSeleccionada.fecha_creacion || preguntaSeleccionada.created_at || '-'}
                    </p>
                  </div>

                  <div className="col-span-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-300 inline-flex items-center gap-1">
                      <FiUser className="text-gray-600 dark:text-gray-300" aria-hidden="true" />
                      Autor
                    </p>
                    <p className="text-sm font-medium dark:text-white mt-1 truncate">{preguntaSeleccionada.autor || 'Anonimo'}</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button 
                  onClick={() => setPreguntaSeleccionada(null)} 
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MisPreguntas;
